import { useCallback, useEffect, useRef, useState } from "react";
import { useLang } from "../../../shared/i18n/useLang";
import { useToast } from "../../../shared/components/SharedUI";
import { overlayClose } from "../../../shared/utils/overlayClose";
import {
  getTickets, getTicket, crearTicket, responderTicket, actualizarTicket,
  getCategoriasTicket,
} from "../services/portalService";
import {
  EstadoBadge, TicketHilo, SelectorAdjuntos, fechaHora, useEstadoLabel,
  acento, ICONO_ESTADO,
} from "../components/TicketPartes";
import styles from "../components/tickets.module.css";

/**
 * /portal/tickets — el residente abre un ticket a la administración y sigue el
 * hilo hasta que se cierra.
 *
 * El hilo es de la filial, no de la persona: si en la casa hay dos accesos al
 * portal, los dos ven los mismos tickets y pueden responder.
 */
export default function PortalTickets() {
  const L = useLang();
  const en = L("es", "en") === "en";
  const label = useEstadoLabel();
  const { showToast, toastEl } = useToast();

  const [tickets, setTickets]       = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [cargando, setCargando]     = useState(true);
  const [vista, setVista]           = useState("lista");   // lista | nuevo
  const [abierto, setAbierto]       = useState(null);      // ticket con su hilo

  // Formulario de un ticket nuevo
  const [asunto, setAsunto]       = useState("");
  const [categoria, setCategoria] = useState("");
  const [mensaje, setMensaje]     = useState("");
  const [adjuntos, setAdjuntos]   = useState([]);
  const [enviando, setEnviando]   = useState(false);

  // Respuesta dentro del hilo
  const [respuesta, setRespuesta] = useState("");
  const [adjRespuesta, setAdjRespuesta] = useState([]);
  const [respondiendo, setRespondiendo] = useState(false);

  // `useToast` arma un `showToast` nuevo en cada render, así que no puede ir
  // como dependencia de `cargar`: el efecto que lo llama se dispararía en cada
  // render y la pantalla quedaría pidiendo la lista sin parar.
  const avisar = useRef(showToast);
  avisar.current = showToast;

  const cargar = useCallback(() => {
    getTickets()
      .then((d) => setTickets(d.results || d))
      .catch((e) => avisar.current(e.message, false))
      .finally(() => setCargando(false));
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  useEffect(() => {
    getCategoriasTicket().then(setCategorias).catch(() => {});
  }, []);

  // Si la administración responde mientras la pantalla está abierta, se
  // refresca sola en vez de esperar a que la persona recargue.
  useEffect(() => {
    function onCambio(e) {
      if (e.detail?.tipo !== "TICKET") return;
      cargar();
      setAbierto((actual) => {
        if (!actual) return actual;
        getTicket(actual.id).then(setAbierto).catch(() => {});
        return actual;
      });
    }
    window.addEventListener("portal:data-changed", onCambio);
    return () => window.removeEventListener("portal:data-changed", onCambio);
  }, [cargar]);

  async function abrirHilo(id) {
    try {
      setAbierto(await getTicket(id));
      // Al abrirlo deja de estar sin leer: la lista tiene que reflejarlo.
      setTickets((prev) => prev.map((t) => (t.id === id ? { ...t, sin_leer: false } : t)));
    } catch (e) {
      showToast(e.message, false);
    }
  }

  function limpiarFormulario() {
    setAsunto(""); setCategoria(""); setMensaje(""); setAdjuntos([]);
  }

  async function enviarTicket(e) {
    e.preventDefault();
    setEnviando(true);
    try {
      const fd = new FormData();
      fd.append("asunto", asunto);
      fd.append("mensaje", mensaje);
      if (categoria) fd.append("categoria", categoria);
      adjuntos.forEach((a) => fd.append("archivos", a));
      const creado = await crearTicket(fd);
      showToast(L("Ticket enviado a la administración.", "Ticket sent to management."), true);
      limpiarFormulario();
      setVista("lista");
      cargar();
      setAbierto(creado);
    } catch (err) {
      showToast(err.message, false);
    } finally {
      setEnviando(false);
    }
  }

  async function enviarRespuesta(e) {
    e.preventDefault();
    setRespondiendo(true);
    try {
      const fd = new FormData();
      fd.append("cuerpo", respuesta);
      adjRespuesta.forEach((a) => fd.append("archivos", a));
      await responderTicket(abierto.id, fd);
      setRespuesta(""); setAdjRespuesta([]);
      setAbierto(await getTicket(abierto.id));
      cargar();
    } catch (err) {
      showToast(err.message, false);
    } finally {
      setRespondiendo(false);
    }
  }

  async function reabrir() {
    try {
      setAbierto(await actualizarTicket(abierto.id, { estado: "ABIERTO" }));
      showToast(L("Ticket reabierto.", "Ticket reopened."), true);
      cargar();
    } catch (err) {
      showToast(err.message, false);
    }
  }

  const activas = categorias.filter((c) => c.activa);

  return (
    <>
      {toastEl}
      <div className={styles.page}>
        <div className={styles.hero}>
          <div className={styles.heroTexto}>
            <span className={styles.heroIcono}>
              <i className="fa-solid fa-headset" />
            </span>
            <div>
              <h1 className={styles.heroTitulo}>{L("Tickets", "Tickets")}</h1>
              <p className={styles.heroSub}>
                {L("Reportá algo roto, hacé una consulta o seguí un reclamo con la administración.",
                   "Report something broken, ask a question or follow up a claim with management.")}
              </p>
            </div>
          </div>
          <div className={styles.heroAcciones}>
            {vista === "lista" ? (
              <button
                type="button"
                className={`${styles.btn} ${styles.btnPrimario}`}
                onClick={() => setVista("nuevo")}
              >
                <i className="fa-solid fa-plus" /> {L("Nuevo ticket", "New ticket")}
              </button>
            ) : (
              <button type="button" className={styles.btn} onClick={() => { setVista("lista"); limpiarFormulario(); }}>
                <i className="fa-solid fa-arrow-left" /> {L("Volver", "Back")}
              </button>
            )}
          </div>
        </div>

        {vista === "nuevo" && (
          <form className={styles.formulario} onSubmit={enviarTicket}>
            <div className={styles.campo}>
              <label className={styles.etiqueta} htmlFor="ticket-asunto">
                {L("Asunto", "Subject")} <span className={styles.requerido}>*</span>
              </label>
              <input
                id="ticket-asunto"
                className="form-control"
                value={asunto}
                onChange={(e) => setAsunto(e.target.value)}
                maxLength={200}
                required
                placeholder={L("Portón del parqueo no cierra", "Parking gate will not close")}
              />
            </div>

            <div className={styles.campo}>
              <label className={styles.etiqueta} htmlFor="ticket-categoria">
                {L("Categoría", "Category")}
              </label>
              <select
                id="ticket-categoria"
                className="form-control"
                value={categoria}
                onChange={(e) => setCategoria(e.target.value)}
              >
                <option value="">{L("Sin categoría", "No category")}</option>
                {activas.map((c) => (
                  <option key={c.id} value={c.id}>{c.nombre}</option>
                ))}
              </select>
            </div>

            <div className={styles.campo}>
              <label className={styles.etiqueta} htmlFor="ticket-mensaje">
                {L("Contanos qué pasa", "Tell us what happened")} <span className={styles.requerido}>*</span>
              </label>
              <textarea
                id="ticket-mensaje"
                className={`form-control ${styles.areaTexto}`}
                value={mensaje}
                onChange={(e) => setMensaje(e.target.value)}
                rows={5}
                required
                placeholder={L(
                  "Desde cuándo pasa, dónde es y cualquier detalle que ayude a resolverlo.",
                  "Since when, where it is and anything that helps solve it.",
                )}
              />
            </div>

            <div className={styles.campo}>
              <span className={styles.etiqueta}>{L("Adjuntos", "Attachments")}</span>
              <SelectorAdjuntos archivos={adjuntos} onChange={setAdjuntos} disabled={enviando} />
              <p className={styles.ayuda}>
                {L("Hasta 3 archivos, PDF o fotos. Las fotos se achican solas antes de subirse.",
                   "Up to 3 files, PDF or photos. Photos are shrunk automatically before upload.")}
              </p>
            </div>

            <div className={styles.filaBotones}>
              <button type="button" className={styles.btn} onClick={() => { setVista("lista"); limpiarFormulario(); }}>
                {L("Cancelar", "Cancel")}
              </button>
              <button type="submit" className={`${styles.btn} ${styles.btnPrimario}`} disabled={enviando}>
                <i className="fa-solid fa-paper-plane" />
                {enviando ? L("Enviando...", "Sending...") : L("Enviar ticket", "Send ticket")}
              </button>
            </div>
          </form>
        )}

        {vista === "lista" && (
          cargando ? (
            <div className={styles.vacio}>
              <p>{L("Cargando...", "Loading...")}</p>
            </div>
          ) : tickets.length === 0 ? (
            <div className={styles.vacio}>
              <i className={`fa-solid fa-inbox ${styles.vacioIcono}`} />
              <p>{L("Todavía no abriste ningún ticket.", "You have not opened any tickets yet.")}</p>
            </div>
          ) : (
            <div className={styles.lista}>
              {tickets.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  className={`${styles.tarjeta} ${acento(t.estado)}`}
                  onClick={() => abrirHilo(t.id)}
                >
                  <span className={styles.tarjetaIcono}>
                    <i className={`fa-solid ${ICONO_ESTADO[t.estado] || "fa-circle"}`} />
                  </span>
                  <div className={styles.tarjetaCuerpo}>
                    <div className={styles.tarjetaFila}>
                      {t.sin_leer && <span className={styles.punto} />}
                      <h2 className={styles.tarjetaAsunto}>{t.asunto}</h2>
                    </div>
                    <p className={styles.tarjetaMeta}>
                      <EstadoBadge estado={t.estado} />
                      {t.categoria_nombre && (
                        <>
                          <span className={styles.separador}>·</span>
                          <span>{t.categoria_nombre}</span>
                        </>
                      )}
                      <span className={styles.separador}>·</span>
                      <span>
                        {t.mensajes_count} {t.mensajes_count === 1
                          ? L("mensaje", "message")
                          : L("mensajes", "messages")}
                      </span>
                      <span className={styles.separador}>·</span>
                      <span>{fechaHora(t.ultimo_mensaje_at || t.created_at, en)}</span>
                    </p>
                  </div>
                  <i className={`fa-solid fa-chevron-right ${styles.tarjetaFlecha}`} />
                </button>
              ))}
            </div>
          )
        )}
      </div>

      {abierto && (
        <div className={styles.overlay} {...overlayClose(() => setAbierto(null))}>
          <div className={styles.modal}>
            <div className={styles.modalCabeza}>
              <div>
                <h2 className={styles.modalTitulo}>{abierto.asunto}</h2>
                <p className={styles.modalMeta}>
                  <EstadoBadge estado={abierto.estado} />
                  {abierto.categoria_nombre && (
                    <>
                      <span className={styles.separador}>·</span>
                      <span>{abierto.categoria_nombre}</span>
                    </>
                  )}
                  <span className={styles.separador}>·</span>
                  <span>{L("Abierto el", "Opened on")} {fechaHora(abierto.created_at, en)}</span>
                </p>
              </div>
              <button
                type="button"
                className={styles.btnIcono}
                onClick={() => setAbierto(null)}
                aria-label={L("Cerrar", "Close")}
              >
                <i className="fa-solid fa-xmark" />
              </button>
            </div>

            <div className={styles.modalCuerpo}>
              <TicketHilo mensajes={abierto.mensajes} esAdmin={false} />

              {abierto.estado === "CERRADO" ? (
                <div className={styles.cerradoAviso}>
                  <p>
                    {L("Este ticket está cerrado. Si el problema volvió, reabrilo y contanos.",
                       "This ticket is closed. If the problem came back, reopen it and tell us.")}
                  </p>
                  <button type="button" className={`${styles.btn} ${styles.btnPrimario}`} onClick={reabrir}>
                    <i className="fa-solid fa-rotate-left" /> {L("Reabrir", "Reopen")}
                  </button>
                </div>
              ) : (
                <form className={styles.composer} onSubmit={enviarRespuesta}>
                  <label className={styles.etiqueta} htmlFor="ticket-respuesta">
                    {L("Responder", "Reply")}
                  </label>
                  <textarea
                    id="ticket-respuesta"
                    className={`form-control ${styles.areaTexto}`}
                    value={respuesta}
                    onChange={(e) => setRespuesta(e.target.value)}
                    rows={3}
                    placeholder={L("Escribí tu mensaje", "Write your message")}
                  />
                  <div className={styles.composerFila}>
                    <SelectorAdjuntos
                      archivos={adjRespuesta}
                      onChange={setAdjRespuesta}
                      disabled={respondiendo}
                    />
                    <button
                      type="submit"
                      className={`${styles.btn} ${styles.btnPrimario}`}
                      disabled={respondiendo || (!respuesta.trim() && !adjRespuesta.length)}
                    >
                      <i className="fa-solid fa-paper-plane" />
                      {respondiendo ? L("Enviando...", "Sending...") : L("Enviar", "Send")}
                    </button>
                  </div>
                  {abierto.estado === "RESUELTO" && (
                    <p className={styles.ayuda}>
                      {L(`La administración lo marcó como ${label("RESUELTO")}. Si escribís, vuelve a la bandeja de la administración.`,
                         `Management marked it as ${label("RESUELTO")}. If you write, it goes back to their inbox.`)}
                    </p>
                  )}
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
